import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api } from '@/api/api-client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VFStatCard } from '@/components/vf/StatCard';
import { ErrorLine } from '@/components/backoffice/platform/PlatformBits';
import { currentLocale, formatDurationClock } from '@/i18n/locale';
import { lastMonths } from './twilioEnv';
import { CarrierCostsSection } from './CarrierCostsSection';

// components/ui/select is typed without `id`; the trigger takes it (label association).
const SelectTriggerWithId = SelectTrigger as unknown as ComponentType<{ id: string; className?: string; children?: ReactNode }>;

/**
 * « Coûts » tab of TwilioConfig (W6): Twilio's usage for a month, picked in a
 * month list labelled in the active language (QW-h11: the native month input
 * printed « October 2026 » in a French UI). When the report fails because
 * Twilio is not configured, the error says so and points to the health
 * check instead of a bare « Impossible de charger ». Below, for the same
 * month, the estimated Asterisk and Zadarma costs (W7, SU-3,
 * CarrierCostsSection).
 */
interface UsageReport {
  month: string;
  start_date: string;
  end_date: string;
  totals: { calls: { count: number; minutes: number; cost: number }; sms: { count: number; cost: number }; recordings: { cost: number }; total_cost: number; currency: string };
  per_tenant: Array<{ tenant_email: string; call_count: number; minutes: number; cost: number }>;
  top_expensive_calls: Array<{ sid: string; to: string; from: string; tenant_email: string | null; duration: number; price: number; date: string }>;
}

const TH = 'h-9 px-3 text-[11px] font-semibold uppercase tracking-wide';

export function TwilioCostsTab({ twilioConfigured }: { twilioConfigured: boolean | null }) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  // Relabel when the language changes.
  const months = useMemo(() => lastMonths(currentLocale()), [i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps
  const [month, setMonth] = useState(months[0]!.value);
  const usage = useQuery({
    queryKey: ['twilio-usage', month],
    queryFn: async () => (await api.functions.invoke('getTwilioUsageReport', { month })).data as UsageReport,
    enabled: twilioConfigured === true,
    retry: false,
  });
  const money = (n: number, currency: string) => n.toLocaleString(currentLocale(), { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const r = usage.data;
  const state = twilioConfigured === false || usage.isError ? 'error' : r ? 'ready' : 'loading';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="space-y-1">
          <Label htmlFor="twilio-usage-month">{t('admin:twilioConfig.usage.month')}</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTriggerWithId id="twilio-usage-month" className="w-56">
              <SelectValue />
            </SelectTriggerWithId>
            <SelectContent>
              {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" onClick={() => void usage.refetch()} disabled={usage.isFetching || twilioConfigured !== true} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${usage.isFetching ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden />
          {t('common:actions.refresh')}
        </Button>
      </div>

      {twilioConfigured === false && <ErrorLine message={t('admin:twilioConfig.usage.notConfigured')} />}
      {twilioConfigured !== false && usage.isError && <ErrorLine message={t('admin:twilioConfig.usage.loadErrorCause')} error={usage.error} onRetry={() => void usage.refetch()} />}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <VFStatCard label={t('admin:twilioConfig.usage.totalCost')} state={state} value={r ? money(r.totals.total_cost, r.totals.currency) : undefined} />
        <VFStatCard label={t('admin:twilioConfig.usage.voice')} state={state} value={r ? money(r.totals.calls.cost, r.totals.currency) : undefined} hint={r ? t('admin:twilioConfig.usage.minutesCalls', { minutes: r.totals.calls.minutes.toLocaleString(currentLocale(), { maximumFractionDigits: 1 }), count: r.totals.calls.count }) : undefined} />
        <VFStatCard label={t('admin:twilioConfig.usage.sms')} state={state} value={r ? money(r.totals.sms.cost, r.totals.currency) : undefined} hint={r ? t('admin:twilioConfig.usage.smsCount', { count: r.totals.sms.count }) : undefined} />
        <VFStatCard label={t('admin:twilioConfig.usage.recordings')} state={state} value={r ? money(r.totals.recordings.cost, r.totals.currency) : undefined} hint={t('admin:twilioConfig.usage.recordingsDesc')} />
      </div>

      {r && r.per_tenant.length > 0 && (
        <section aria-labelledby="twilio-cost-tenant" className="space-y-2">
          <h2 id="twilio-cost-tenant" className="text-base font-semibold">{t('admin:twilioConfig.usage.costByTenant')}</h2>
          <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
            <table className="w-full text-[13px]">
              <caption className="sr-only">{t('admin:twilioConfig.usage.costByTenant')}</caption>
              <thead style={{ background: 'var(--vf-surface-1)', color: 'var(--vf-text-3)' }}>
                <tr>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.numbers.columns.organisation')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.usage.tableCalls')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.usage.tableMinutes')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.usage.tableCost')}</th>
                </tr>
              </thead>
              <tbody>
                {r.per_tenant.map((row) => (
                  <tr key={row.tenant_email} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <td className="h-10 px-3">{row.tenant_email || t('admin:twilioConfig.numbers.unassigned')}</td>
                    <td className="h-10 px-3 text-right tabular-nums">{row.call_count}</td>
                    <td className="h-10 px-3 text-right tabular-nums">{row.minutes.toLocaleString(currentLocale(), { maximumFractionDigits: 1 })}</td>
                    <td className="h-10 px-3 text-right tabular-nums">{money(row.cost, r.totals.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {r && r.top_expensive_calls.length > 0 && (
        <section aria-labelledby="twilio-cost-top" className="space-y-2">
          <h2 id="twilio-cost-top" className="text-base font-semibold">{t('admin:twilioConfig.usage.topExpensiveCalls')}</h2>
          <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
            <table className="w-full text-[13px]">
              <caption className="sr-only">{t('admin:twilioConfig.usage.topExpensiveCalls')}</caption>
              <thead style={{ background: 'var(--vf-surface-1)', color: 'var(--vf-text-3)' }}>
                <tr>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.usage.tableDate')}</th>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.usage.tableFrom')}</th>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.usage.tableTo')}</th>
                  <th scope="col" className={`${TH} text-left`}>{t('admin:twilioConfig.numbers.columns.organisation')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.usage.tableDuration')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('admin:twilioConfig.usage.tableCost')}</th>
                </tr>
              </thead>
              <tbody>
                {r.top_expensive_calls.map((c) => (
                  <tr key={c.sid} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                    <td className="h-10 px-3">{new Date(c.date).toLocaleString(currentLocale(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="h-10 px-3 font-mono text-xs">{c.from}</td>
                    <td className="h-10 px-3 font-mono text-xs">{c.to}</td>
                    <td className="h-10 max-w-[12rem] truncate px-3" title={c.tenant_email ?? ''}>{c.tenant_email || '—'}</td>
                    <td className="h-10 px-3 text-right tabular-nums">{formatDurationClock(c.duration)}</td>
                    <td className="h-10 px-3 text-right tabular-nums">{money(c.price, r.totals.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {r && (
        <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>
          {t('admin:twilioConfig.usage.footerPeriod', { start: new Date(r.start_date).toLocaleDateString(currentLocale()), end: new Date(r.end_date).toLocaleDateString(currentLocale()) })}{' '}
          <a href="https://console.twilio.com/us1/monitor/usage/all" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--vf-accent-text)' }}>
            {t('admin:twilioConfig.usage.billingDetails')}
          </a>
        </p>
      )}
      <p className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.usage.twilioOnly')}</p>
      <CarrierCostsSection month={month} />
    </div>
  );
}
